import { useEffect, useRef, useState } from 'react';
import { WS_BASE_URL } from '../config/endpoints.js';
import { generateRequestId } from '../observability/requestId.js';
import { logInfo, logError, logWarn } from '../observability/logger.js';
import {
  MAX_RECONNECT_ATTEMPTS,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_TIMEOUT_MS,
  computeReconnectDelay,
  shouldRetryReconnect,
} from './wsConnectionPolicy.js';

const useWebSocket = (token) => {
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);

  const [socket, setSocket] = useState(null);
  const [connectionState, setConnectionState] = useState('closed');

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearHeartbeatTimers = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  };

  const scheduleReconnect = (connect) => {
    if (!shouldRetryReconnect(reconnectAttemptsRef.current)) {
      setConnectionState('degraded');
      logWarn('ws.reconnect.max_attempts_reached', { attempts: reconnectAttemptsRef.current });
      return;
    }

    reconnectAttemptsRef.current += 1;
    setConnectionState('reconnecting');

    const delayMs = computeReconnectDelay(reconnectAttemptsRef.current);

    clearReconnectTimer();
    reconnectTimerRef.current = setTimeout(() => {
      connect();
    }, delayMs);

    logInfo('ws.reconnect.scheduled', {
      attempts: reconnectAttemptsRef.current,
      delay_ms: delayMs,
    });
  };

  const startHeartbeat = (ws) => {
    clearHeartbeatTimers();

    heartbeatIntervalRef.current = setInterval(() => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }

      const heartbeatRequestId = generateRequestId('ws-ping');
      ws.send(JSON.stringify({ action: 'ping', request_id: heartbeatRequestId }));

      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
      }

      heartbeatTimeoutRef.current = setTimeout(() => {
        setConnectionState('degraded');
        logWarn('ws.heartbeat.timeout', { request_id: heartbeatRequestId });
        ws.close();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_INTERVAL_MS);
  };

  useEffect(() => {
    if (!token) {
      clearReconnectTimer();
      clearHeartbeatTimers();
      if (socketRef.current) {
        socketRef.current.close();
      }
      socketRef.current = null;
      setSocket(null);
      setConnectionState('closed');
      return undefined;
    }

    let shouldReconnect = true;

    const connect = () => {
      const connectionRequestId = generateRequestId('ws');
      setConnectionState(reconnectAttemptsRef.current > 0 ? 'reconnecting' : 'connecting');

      const ws = new WebSocket(`${WS_BASE_URL}/ws/ws?token=${token}&request_id=${connectionRequestId}`);
      socketRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        setConnectionState('open');
        logInfo('ws.connected', { request_id: connectionRequestId });

        ws.send(JSON.stringify({ action: 'get_olts_info', request_id: generateRequestId('ws-olts') }));
        ws.send(
          JSON.stringify({
            action: 'get_all_splitters',
            request_id: generateRequestId('ws-splitters'),
          })
        );

        setSocket(ws);
        startHeartbeat(ws);
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          if (parsed.action === 'ping' && parsed.status === 'success') {
            if (heartbeatTimeoutRef.current) {
              clearTimeout(heartbeatTimeoutRef.current);
              heartbeatTimeoutRef.current = null;
            }
            if (connectionState !== 'open') {
              setConnectionState('open');
            }
          }
        } catch {
          // noop: the app-specific message handlers process business messages.
        }
      };

      ws.onerror = (error) => {
        logError('ws.error', { message: String(error) });
      };

      ws.onclose = () => {
        clearHeartbeatTimers();
        setSocket(null);

        if (!shouldReconnect) {
          setConnectionState('closed');
          return;
        }

        setConnectionState('degraded');
        logWarn('ws.disconnected');
        scheduleReconnect(connect);
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      clearReconnectTimer();
      clearHeartbeatTimers();
      if (socketRef.current) {
        socketRef.current.close();
      }
      socketRef.current = null;
      setSocket(null);
      setConnectionState('closed');
    };
  }, [token]);

  return { socket, connectionState };
};

export default useWebSocket;
